import { IRowData } from "@multiweb/types/contracts";
import { IDataSourceLoadInfo } from "../../Global/Base/DataSources/Interfaces";
import { Event } from "../../Global/Base/Events";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarCell } from "./CalendarGrids/CalendarCell";
import { CalendarEvent } from "./CalendarGrids/CalendarEvent";
import { CalendarEventItem } from "./CalendarGrids/CalendarEventItem";
import { DataGrid } from "./DataGrid";
import { CalendarItem } from "./DataGridSubcontrols/Columns/CalendarItem";
import { CalendarDataRow } from "./DataGridSubcontrols/Components/CalendarDataRow";
import { AggregatedObjectDataSource, IObjectExtendedDataSource } from "./DataSource/AggregatedObjectDataSource";
import { ICalendarTooltipCreator } from "./ICalendarTooltip";
import { IDataGridDataShownEventData } from "./IEvent";

export class CalendarGrid extends DataGrid<CalendarDataRow> {
    public readonly firstDayOfWeekChanged: Event<CalendarGrid, DayOfWeek> = new Event<CalendarGrid, DayOfWeek>();
    public readonly intervalChanged: Event<CalendarGrid, IntervalDirection> = new Event<CalendarGrid, IntervalDirection>();
    public readonly calendarChangeRequest: Event<CalendarGrid, { type: CalendarType; date: Date }> = new Event<CalendarGrid, { type: CalendarType; date: Date }>();
    protected _tooltipCreator: ICalendarTooltipCreator;
    protected override _dataSource: AggregatedObjectDataSource<IObjectExtendedDataSource>;
    protected _firstDayOfWeek: DayOfWeek = DayOfWeek.monday;
    protected _currentDate: Date;

    protected _dayCells: Map<string, CalendarCell> = new Map<string, CalendarCell>();
    protected _selectedCell: CalendarCell;
    protected _mouseOverItem: CalendarEvent;
    public uniqueDSId?: string;

    public get isActive(): boolean {
        return this.interactive && this.enabled;
    }

    public get selectedCell(): CalendarCell {
        return this._selectedCell;
    }

    public get mouseOverItem(): CalendarEvent {
        return this._mouseOverItem;
    }

    public set firstDayOfWeek(v: DayOfWeek) {
        if (this._firstDayOfWeek == v)
            return;
        this._firstDayOfWeek = v;

        this.firstDayOfWeekChanged.trigger(this, v);
        this._redraw();
    }

    public get firstDayOfWeek(): DayOfWeek {
        return this._firstDayOfWeek;
    }

    public set currentDate(v: Date) {
        if (v == undefined || v == this._currentDate)
            return;
        this._currentDate = v;

        this._redraw();
    }

    public get currentDate(): Date {
        return this._currentDate;
    }

    public get tooltipCreator(): ICalendarTooltipCreator {
        return this._tooltipCreator;
    }

    public override clearSelectedRows(): void {
        while (this.selectedRows.length != 0) {
            this.selectedRows.pop().isSelected = false;
        }
    }

    public addSelectedRow(row: CalendarDataRow): void {
        row.selectWithCheckChanges();
    }

    protected override _constructor(): void {
        super._constructor();
        this.classList.add("calendar-grid");

        this._dataRowFactory = () => new CalendarDataRow();
    }

    public override initialize(): void {
        if (this._dataSource) {
            this.dataSource.dataLoaded.add((_sender, eventData) => {
                if (eventData && eventData.additionalData && eventData.additionalData.uniqueId) {
                    this.uniqueDSId = eventData.additionalData.uniqueId;
                } else {
                    throw new Error("eventData.additionalData.uniqueId is undefined.");
                }
                if (!this.visible)
                    return;
                if (eventData.refreshedData)
                    this._refreshRowsWithData(eventData);
                else {
                    this._showData(eventData);
                }
                this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
            });
        }
    }

    /**
     * Je zavoláno vždy po zobrazení DG pokud byl skrytý ať už styly nebo právama
     * @protected
     */
    protected override _recalculateAfterVisible(): void {
        // do nothing!
    }

    public selectedCellTimeSpan(): { start: Date; end: Date } {
        throw new Error("Method selectedCellTimeSpan not implemented");
    }

    public getCalendarItemById(uniqueId: string): CalendarItem {
        return (this._columns as CalendarItem[]).find(item => item.itemId == uniqueId);
    }

    public actualizeDisplayedInformation(_ce: CalendarEvent): void {
        // Prazdna metoda
    }

    protected override _showData(eventData: IDataSourceLoadInfo): void {
        const f_fetchNextBatch = async () => {
            //this._logger.log("Fetching data: startIndex: " + eventData.startIndex + " rowCount: " + this.DataRows.length + " loadTo: " + this.loadToIndex);

            //if (!this._allDataRead && (this._listDiv.offsetHeight >= this._listDiv.scrollHeight) && this._listDiv.offsetHeight > 0)
            //    this.dataSource.getNextData(this.startIndex, this.fetchCount, eventData.keepSelectedRecords);

            if (eventData.keepSelectedRecords !== false && !(eventData.lastBatch && !eventData.refreshedData)) {
                await this.keepSelectedRows();
            }
        };

        this._allDataRead = eventData.lastBatch;

        this._bindingData = true;

        this._createRowsByDS(eventData.dataGenerator(), eventData.additionalData.uniqueId);

        this._renderData(eventData);

        this._bindingData = false;

        // ve vyjimecnych pripadech dochazelo k tomu, ze se data docitala jeste pred vykreslenim DG, a proto se spatne pocitala vyska pro fetch count
        if (!this.isAttached) {
            let evAtt: any;
            this.registerEventListener("attached", evAtt = () => {
                f_fetchNextBatch();

                this.unregisterEventListener("attached", evAtt);
            });
        } else {
            f_fetchNextBatch();
        }
    }

    protected _redraw(): Promise<void> {
        this._mainListDiv.innerHTML = "";
        this._dayCells.clear();
        this._renderHeader();
        this._renderDataContainer();

        return Promise.resolve();
    }

    protected _createRowsByDS(data: Iterable<IRowData>, uniqueId: string): void {
        const eventsList: CalendarEvent[] = [];
        for (const row of data) {
            //this._attachEvent(this._createEventByDS(row, uniqueId));
            eventsList.push(this._createEventByDS(row, uniqueId));
        }
        const sortedList = eventsList.sort((a, b) => a.eventDuration > b.eventDuration ? -1 : 1);

        for (const row of sortedList) {
            this._attachEvent(row);
        }
    }

    protected _updateEventPosition(cv: CalendarEvent, cell: CalendarCell): void {
        let items = cv.getCalendarEventItems();
        const oldRowNumCV = cv.rowNum;

        for (const item of items) {
            const rRowNum = item.realRowNum;
            item.rowNum = rRowNum;
        }

        if (cv.rowNum != oldRowNumCV) {
            items = cv.getCalendarEventItems();
            for (const item of items) {
                const dayCell = this._dayCells.get(item.eventId);
                if (dayCell && cv.rowNum > item.rowNum) {
                    dayCell.addEmptyItem(item, "before", cv.rowNum - item.rowNum);
                    const nextElement = item.nextElementSibling;
                    if (nextElement != null && nextElement instanceof CalendarEventItem && nextElement.isEmpty) {
                        nextElement.remove();
                    }
                }
            }
        }
        if (cell.eventItems != undefined && cv.rowNum < cell.eventItems.length) {
            items = cv.getCalendarEventItems();
            for (const item of items) {
                const dayCell = this._dayCells.get(item.eventId);
                if (dayCell && dayCell.idOfCell == cell.idOfCell && dayCell.eventItems != undefined && dayCell.eventItems.length < cell.eventItems.length) {
                    dayCell.addEmptyItem(item, "after", cell.eventItems.length - dayCell.eventItems.length);
                }
            }
        }
    }

    public clear(): void {
        this.clearDataRows();
        this.clearSelectedRows();
        this._redraw();
        this.skippedRowsCount = 0;
    }

    protected _attachEvent(_cv: CalendarEvent): void {
        throw new Error("Method _attachEvent not implemented");
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected _createEventByDS(_data: IRowData, _uniqueId: string): CalendarEvent {
        throw new Error("Method _createEventByDS not implemented");
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected _createRowByDS(data: IRowData, uniqueId: string): CalendarDataRow {
        const dataRow = this._dataRowFactory(this, data);

        dataRow.uniqueId = uniqueId;
        dataRow.init(data, this, this._createRowControlElement(dataRow));

        this.dataRows.push(dataRow);

        return dataRow;
    }

    /**
     * Vykresli pozadovanou "stranku" zaznamu
     */
    protected override _renderData(eventData?: IDataSourceLoadInfo): void {
        if (!this._dataTable)
            return;

        for (let i: number = this._dataTable.childElementCount; i < this.dataRows.length; i++) {
            this._dataTable.appendChild(this.dataRows[i].render());
        }

        if (this.emptyMessage && this.dataRows.length > 0) {
            this.emptyMessage.visible = false;
        }

        this._bindingData = false;
        this.resizeGrid(eventData);
    }

    /**
     * Vola se pokud je resizovan formular nebo cokoliv jineho na co potrebuje grid zareagovat a docist zaznamy kvuli ztrate scrollbaru
     */
    public override resizeGrid(_eventData?: IDataSourceLoadInfo): void {
        // Prazdna metoda
    }

    /**
     * Umožňuje zaregistrovat vlastní tovární funkci, která se použivá při vytváření tooltipu.
     * @param tooltipCreator
     */
    public registerTooltipCreator(tooltipCreator: ICalendarTooltipCreator): void {
        this._tooltipCreator = tooltipCreator;
    }

    protected override _refreshRowsWithData(eventData: IDataSourceLoadInfo): void {
        this._showData(eventData);
    }
}

export enum DayOfWeek {
    sunday,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
}

export enum IntervalDirection {
    next,
    prev,
}

export enum CalendarType {
    day,
    month,
    week,
    //year,
}

registerCustomElement("tsc-calendardatagrid", CalendarGrid);
